import React, { useRef, useState, useEffect } from "react";
import { useReportExport, ExportOptions } from "@/hooks/useReportExport";

interface Props {
  /** Pre-fill start date when panel opens. Defaults to 30 days ago. */
  defaultStartDate?: Date;
  /** Pre-fill end date when panel opens. Defaults to today. */
  defaultEndDate?: Date;
  reportType?: ExportOptions["reportType"];
}

function toInputValue(d: Date) {
  return d.toISOString().slice(0, 10); // "YYYY-MM-DD"
}

function fromInputValue(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export default function ReportExportButton({ defaultStartDate, defaultEndDate, reportType }: Props) {
  const { exportState, startExport, reset, threshold } = useReportExport();

  const [open, setOpen] = useState(false);
  const [startVal, setStartVal] = useState(() =>
    toInputValue(defaultStartDate ?? (() => { const d = new Date(); d.setDate(d.getDate() - 30); return d; })())
  );
  const [endVal, setEndVal] = useState(() => toInputValue(defaultEndDate ?? new Date()));
  const [format, setFormat] = useState<"xlsx" | "csv">("xlsx");

  const panelRef = useRef<HTMLDivElement>(null);

  // Sync pre-fills when parent updates dates
  useEffect(() => {
    if (defaultStartDate) setStartVal(toInputValue(defaultStartDate));
  }, [defaultStartDate?.toDateString()]);
  useEffect(() => {
    if (defaultEndDate) setEndVal(toInputValue(defaultEndDate));
  }, [defaultEndDate?.toDateString()]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  function handleExport() {
    const start = fromInputValue(startVal);
    const end = fromInputValue(endVal);
    startExport({ startDate: start, endDate: end, format, reportType });
  }

  function handleClose() {
    setOpen(false);
    reset();
  }

  const { status, downloadUrl, expiresAt, recipientEmail, errorMessage } = exportState;
  const busy = status === "checking" || status === "downloading" || status === "polling";
  const dayRange = Math.round((fromInputValue(endVal).getTime() - fromInputValue(startVal).getTime()) / (1000 * 60 * 60 * 24));
  const willBeAsync = dayRange > threshold;

  return (
    <div className="relative" ref={panelRef}>
      {/* Trigger button */}
      <button
        onClick={() => { if (open) handleClose(); else setOpen(true); }}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
      >
        <i className="fa-solid fa-file-export text-xs" aria-hidden="true" />
        Export
        {(status === "queued" || status === "completed") && (
          <span className="w-2 h-2 rounded-full bg-cornflower-blue-500 ml-0.5" />
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl w-80 p-4 space-y-4">

          {/* Idle / input state */}
          {(status === "idle" || status === "checking") && (
            <>
              <div>
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">Export Report</p>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block">
                      <span className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Start date</span>
                      <input
                        type="date"
                        value={startVal}
                        onChange={e => setStartVal(e.target.value)}
                        className="w-full px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">End date</span>
                      <input
                        type="date"
                        value={endVal}
                        onChange={e => setEndVal(e.target.value)}
                        max={toInputValue(new Date())}
                        className="w-full px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm"
                      />
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 dark:text-slate-400">Format</span>
                    <div className="flex rounded-lg border border-slate-200 dark:border-slate-600 overflow-hidden text-sm">
                      {(["xlsx", "csv"] as const).map(f => (
                        <button
                          key={f}
                          onClick={() => setFormat(f)}
                          className={`px-3 py-1 font-medium transition-colors ${
                            format === f
                              ? "bg-cornflower-blue-600 text-white"
                              : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50"
                          }`}
                        >
                          .{f}
                        </button>
                      ))}
                    </div>
                    <span className="text-xs text-slate-400 ml-auto">{dayRange}d</span>
                  </div>
                  {willBeAsync && (
                    <div className="flex items-start gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/50 rounded-lg p-2.5">
                      <i className="fa-solid fa-envelope text-blue-500 text-xs mt-0.5 shrink-0" aria-hidden="true" />
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        Range &gt;{threshold} days — report will be emailed to you as a download link.
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={handleExport}
                disabled={busy || !startVal || !endVal}
                className="w-full py-2 rounded-lg bg-cornflower-blue-600 hover:bg-cornflower-blue-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {busy ? (
                  <><i className="fa-solid fa-circle-notch fa-spin text-xs" aria-hidden="true" /> Preparing…</>
                ) : willBeAsync ? (
                  <><i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" /> Email Report</>
                ) : (
                  <><i className="fa-solid fa-download text-xs" aria-hidden="true" /> Download Now</>
                )}
              </button>
            </>
          )}

          {/* Downloading (sync in progress) */}
          {status === "downloading" && (
            <div className="text-center py-2 space-y-2">
              <i className="fa-solid fa-circle-notch fa-spin text-cornflower-blue-500 text-2xl" aria-hidden="true" />
              <p className="text-sm text-slate-600 dark:text-slate-300">Generating report…</p>
              <p className="text-xs text-slate-400">Your download will start automatically.</p>
            </div>
          )}

          {/* Queued / polling */}
          {(status === "queued" || status === "polling") && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                <i className="fa-solid fa-circle-notch fa-spin" aria-hidden="true" />
                <span className="text-sm font-semibold">
                  {status === "queued" ? "Report queued" : "Processing…"}
                </span>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                We're generating your report. A download link will be emailed to{" "}
                <strong>{recipientEmail ?? "you"}</strong>.
              </p>
              <p className="text-xs text-slate-400">
                You can close this — we'll email you when it's ready.
              </p>
              <button
                onClick={handleClose}
                className="w-full py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                Close
              </button>
            </div>
          )}

          {/* Completed */}
          {status === "completed" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                <i className="fa-solid fa-circle-check" aria-hidden="true" />
                <span className="text-sm font-semibold">Report ready!</span>
              </div>
              {downloadUrl && (
                <a
                  href={downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors"
                >
                  <i className="fa-solid fa-download text-xs" aria-hidden="true" />
                  Download Report
                </a>
              )}
              {expiresAt && (
                <p className="text-xs text-slate-400 text-center">
                  Link expires {new Date(expiresAt).toLocaleDateString()}
                </p>
              )}
              <button
                onClick={handleClose}
                className="w-full py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                Close
              </button>
            </div>
          )}

          {/* Failed */}
          {status === "failed" && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
                <span className="text-sm font-semibold">Export failed</span>
              </div>
              <p className="text-xs text-red-600 dark:text-red-400">
                {errorMessage ?? "An error occurred. Please try again."}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={reset}
                  className="flex-1 py-1.5 rounded-lg bg-cornflower-blue-600 hover:bg-cornflower-blue-700 text-white text-sm font-medium transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
